import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useAuth } from '../../../components/AuthProvider';
import { apiUrl } from '../../../lib/api';
import { generateStructuredAssets } from '../../../utils/generateStructuredAssets';
import { convertAndDownload, availableTargetFormats } from '../../../utils/convertContent';

// ─── Font Styles ──────────────────────────────────────────────────────────────
const fontGrotesk  = { fontFamily: "'Rajdhani', 'Inter', sans-serif" };
const fontMono     = { fontFamily: "'Share Tech Mono', 'JetBrains Mono', monospace" };
const fontOrbitron = { fontFamily: "'Orbitron', monospace" };

// ─── VORTEX Theme Tokens ──────────────────────────────────────────────────────
const C = {
  bgVoid:       '#04070d',
  bgDeep:       '#070c14',
  bgPanel:      '#0a1120',
  bgCard:       '#0d1628',
  bgCardHover:  '#111e35',
  teal:         '#00e5c8',
  tealDim:      '#00a896',
  tealGlow:     'rgba(0,229,200,0.12)',
  tealGlowStr:  'rgba(0,229,200,0.22)',
  purple:       '#7c3fff',
  purpleDim:    '#5a2dcc',
  purpleGlow:   'rgba(124,63,255,0.15)',
  green:        '#00ff88',
  greenDim:     '#00cc6a',
  greenGlow:    'rgba(0,255,136,0.15)',
  amber:        '#ffb020',
  amberGlow:    'rgba(255,176,32,0.15)',
  red:          '#ff3d5a',
  textPrimary:  '#d4e8f0',
  textSec:      '#6a8499',
  textDim:      '#334455',
  border:       'rgba(0,229,200,0.12)',
  borderBright: 'rgba(0,229,200,0.3)',
  borderPurple: 'rgba(124,63,255,0.25)',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cleanMarkdown(text) {
  if (!text) return '';
  return text
    ?.replace(/```[\s\S]*?```/g, (match) =>
      match?.replace(/```[a-z]*\n?/g, '')?.replace(/```/g, '')?.trim()
    )
    ?.replace(/`([^`]+)`/g, '$1')
    ?.replace(/^#{1,6}\s+/gm, '')
    ?.replace(/\*\*([^*]+)\*\*/g, '$1')
    ?.replace(/\*([^*]+)\*/g, '$1')
    ?.replace(/^[-*+]\s+/gm, '• ')
    ?.replace(/^\d+\.\s+/gm, (m) => m)
    ?.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    ?.replace(/^>\s+/gm, '')
    ?.replace(/---+/g, '')
    ?.trim();
}

function formatOutputAsReadable(text) {
  if (!text) return '';
  const cleaned = cleanMarkdown(text);
  const paragraphs = cleaned?.split(/\n{2,}/)?.map(p => p?.trim())?.filter(Boolean);
  return paragraphs;
}

function extractActionsFromCards(cards, missionTitle) {
  const actions = [];
  if (cards && cards?.length > 0) {
    for (let i = 0; i < cards?.length; i++) {
      const card = cards?.[i];
      const output = card?.full_output || card?.output || card?.description || '';
      const lines = output?.split('\n')?.filter(l => l?.trim()?.length > 10);
      const script = lines?.slice(0, 8)?.join('\n') || output?.slice(0, 400);
      const impactLevel = i === 0 ? 'REVENUE' : i === 1 ? 'GROWTH' : i === 2 ? 'GROWTH' : 'SUPPORT';
      actions?.push({
        title: card?.title || `Action ${i + 1}`,
        description: lines?.slice(0, 2)?.join(' ')?.slice(0, 200) || 'Review the output and take action.',
        script: script || '',
        time: ['15 min', '30 min', '45 min', '20 min']?.[i] || '30 min',
        impact: impactLevel,
        followUp: `Follow up on: ${card?.title || missionTitle || 'this mission'}`,
      });
    }
  }
  if (actions?.length < 3) {
    const defaults = [
      { title: 'Review & Refine Output', description: 'Go through the produced content and customize it for your specific context and brand voice.', script: `Review Checklist\n\n1. Read through all produced content\n2. Identify sections that need customization\n3. Add your brand voice and specific details\n4. Remove any generic placeholders\n5. Finalize and save your version`, time: '20 min', impact: 'REVENUE', followUp: `Refine output for: ${missionTitle || 'this mission'}` },
      { title: 'Implement Top Priority Item', description: 'Take the highest-value output from this mission and put it into action immediately.', script: `Implementation Steps\n\n1. Identify the #1 priority output\n2. Set a deadline for implementation\n3. Assign ownership (yourself or team)\n4. Create a simple action checklist\n5. Execute and track results`, time: '30 min', impact: 'GROWTH', followUp: `Implement execution from: ${missionTitle || 'this mission'}` },
      { title: 'Run Follow-Up Mission', description: "Build on this mission's execution by running a targeted follow-up to go deeper on a specific area.", script: `Follow-Up Mission Ideas\n\n- Deep dive into the most promising area\n- Create implementation assets\n- Build on the research findings\n- Produce supporting content\n- Measure and optimize execution`, time: '15 min', impact: 'SUPPORT', followUp: `Deep dive follow-up on: ${missionTitle || 'this mission'}` },
    ];
    while (actions?.length < 3) actions?.push(defaults?.[actions?.length]);
  }
  return actions;
}

function buildFileRows(data) {
  const { actionCards = [], completedSteps = {} } = data;
  const rows = [];
  if (actionCards?.length > 0) {
    actionCards?.forEach((card, i) => {
      const title = card?.title || `output-${i + 1}`;
      const slug = title?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
      const output = card?.full_output || card?.output || card?.description || '';
      rows?.push({ filename: `${slug}.md`, size: output ? `${(output?.length / 1024)?.toFixed(1)} KB` : '0 KB', output, title });
    });
  } else if (completedSteps && Object.keys(completedSteps)?.length > 0) {
    Object.values(completedSteps)?.forEach((step, i) => {
      const action = step?.action || step?.id || `output-${i + 1}`;
      const slug = action?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
      const output = step?.output || '';
      rows?.push({ filename: `${slug}.txt`, size: output ? `${(output?.length / 1024)?.toFixed(1)} KB` : '0 KB', output, title: action });
    });
  }
  return rows;
}

function generateWhatYouHave(data) {
  const { actionCards = [], completedSteps = {}, intent, steps_completed = 0 } = data;
  const bullets = [];
  if (actionCards?.length > 0) {
    actionCards?.slice(0, 5)?.forEach(card => { if (card?.title) bullets?.push(`${card.title} — ready to deploy immediately`); });
  } else if (completedSteps && Object.keys(completedSteps)?.length > 0) {
    Object.values(completedSteps)?.slice(0, 5)?.forEach(step => { const action = step?.action || step?.id || ''; if (action) bullets?.push(`${action} — executed and ready`); });
  }
  if (bullets?.length === 0) {
    bullets?.push(`A complete execution plan for: ${intent || 'your mission'}`);
    bullets?.push('Actionable next steps you can implement today');
    bullets?.push('Ready-to-deploy assets and frameworks');
    if (steps_completed > 0) bullets?.push(`${steps_completed} expert-level tasks executed on your behalf`);
  }
  return bullets?.slice(0, 5);
}

function generateWhyItMatters(data) {
  const { summary_strategic, intent, steps_completed = 0 } = data;
  if (summary_strategic) return summary_strategic;
  return `This mission executed ${steps_completed} specialized tasks that would typically take hours of manual research and execution. The assets are designed to be immediately deployable — saving you time and delivering professional-grade execution for: ${intent || 'your goal'}.`;
}

function FormattedSummary({ text, style }) {
  if (!text) return null;
  // Split on bold markers like **What to do next:**
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  const blocks = [];
  let currentTitle = null;
  let currentBody = '';
  for (const part of parts) {
    if (part.startsWith('**') && part.endsWith('**')) {
      if (currentTitle || currentBody) {
        blocks.push({ title: currentTitle, body: currentBody.trim() });
      }
      currentTitle = part.replace(/\*\*/g, '').trim();
      currentBody = '';
    } else {
      currentBody += part;
    }
  }
  if (currentTitle || currentBody) {
    blocks.push({ title: currentTitle, body: currentBody.trim() });
  }
  // Handle # heading at start of first block
  let heading = null;
  if (blocks.length > 0 && blocks[0].body && blocks[0].body.startsWith('#')) {
    const lines = blocks[0].body.split('\n');
    heading = lines[0].replace(/^#+\s*/, '').trim();
    blocks[0].body = lines.slice(1).join('\n').trim();
    if (!blocks[0].body && !blocks[0].title) blocks.shift();
  }
  return (
    <div style={style}>
      {heading && <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.06em', marginBottom: 14, textTransform: 'uppercase' }}>{heading}</div>}
      {blocks.map((b, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          {b.title && <span style={{ fontWeight: 700, color: '#2DC896' }}>{b.title} </span>}
          <span>{b.body}</span>
        </div>
      ))}
    </div>
  );
}

function generatePlainSummary(data) {
  const { summary_simple, summary_strategic, intent, steps_completed = 0 } = data;
  if (summary_simple) return summary_simple;
  if (summary_strategic) return summary_strategic;
  return `Your mission is complete. ${steps_completed} tasks were executed to deliver assets for: ${intent || 'your goal'}.`;
}

// ─── MD / CSV / Notion Helpers ────────────────────────────────────────────────

const TABULAR_KEYWORDS = ['calendar','sequence','leads','pipeline','list','schedule','prospects','contacts','accounts','crm','outreach','campaign','tasks','tracker','matrix','dashboard','rolodex','roster','plan','funnel'];

function isLikelyTabular(name) {
  if (!name) return false;
  const lower = String(name).toLowerCase();
  return TABULAR_KEYWORDS.some(k => lower.includes(k));
}

function csvEscape(v) {
  const s = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

function markdownToCsv(input) {
  if (!input) return '';
  const text = String(input);
  const lines = text.split(/\r?\n/);
  // 1) Markdown table
  const tbl = lines.filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'));
  if (tbl.length >= 3) {
    const data = tbl.filter(l => !/^\|[\s\-:|]+\|$/.test(l.trim()));
    if (data.length >= 2) {
      return data.map(l => l.trim().slice(1, -1).split('|').map(c => csvEscape(c.trim())).join(',')).join('\n');
    }
  }
  // 2) JSON array of objects
  const jm = text.match(/\[\s*\{[\s\S]*?\}\s*\]/);
  if (jm) {
    try {
      const arr = JSON.parse(jm[0]);
      if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object') {
        const headers = [...new Set(arr.flatMap(o => Object.keys(o)))];
        const rows = [headers.map(csvEscape).join(',')];
        arr.forEach(o => rows.push(headers.map(h => csvEscape(o[h])).join(',')));
        return rows.join('\n');
      }
    } catch (e) {}
  }
  // 3) Section / Content fallback
  const sections = [];
  let h = '';
  let body = [];
  for (const line of lines) {
    const m = line.match(/^#{1,6}\s+(.+)/);
    if (m) {
      if (h || body.length) sections.push([h, body.join(' ').trim()]);
      h = m[1].trim();
      body = [];
    } else if (line.trim()) {
      body.push(line.trim());
    }
  }
  if (h || body.length) sections.push([h, body.join(' ').trim()]);
  if (sections.length > 0) {
    return ['Section,Content'].concat(sections.map(s => csvEscape(s[0]) + ',' + csvEscape(s[1]))).join('\n');
  }
  return text;
}

// ─── CopyButton ───────────────────────────────────────────────────────────────

const CopyButton = ({ text, label = 'COPY' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator?.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={handleCopy} style={{
      display: 'flex', alignItems: 'center', gap: 5,
      background: copied ? `rgba(0,255,136,0.12)` : `rgba(0,229,200,0.06)`,
      border: `1px solid ${copied ? 'rgba(0,255,136,0.4)' : C.border}`,
      borderRadius: 3, padding: '4px 9px', ...fontMono, fontSize: 9, fontWeight: 700,
      color: copied ? C.green : C.tealDim, cursor: 'pointer', transition: 'all 0.2s', letterSpacing: '1px',
    }}>
      {copied ? <><Check style={{ width: 10, height: 10 }} /> COPIED</> : <><Copy style={{ width: 10, height: 10 }} /> {label}</>}
    </button>
  );
};

// ─── NotionCopyButton ─────────────────────────────────────────────────────────
// Copies markdown to clipboard. Notion auto-converts on paste.
// Accepts getContent as a string or async loader function.

const NotionCopyButton = ({ getContent, label = 'COPY NOTION FORMAT' }) => {
  const [state, setState] = useState('idle');
  const handleCopy = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const content = typeof getContent === 'function' ? await getContent() : getContent;
      if (content == null || content === '') { setState('idle'); return; }
      await navigator.clipboard.writeText(String(content));
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch (err) {
      console.error('Notion copy error:', err);
      setState('idle');
    }
  };
  const isCopied = state === 'copied';
  const isLoading = state === 'loading';
  return (
    <button onClick={handleCopy} disabled={isLoading} style={{
      display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
      background: isCopied ? 'rgba(0,255,136,0.12)' : 'rgba(0,229,200,0.06)',
      border: '1px solid ' + (isCopied ? 'rgba(0,255,136,0.4)' : C.border),
      borderRadius: 3, padding: '4px 9px', ...fontMono, fontSize: 9, fontWeight: 700,
      color: isCopied ? C.green : C.tealDim, cursor: isLoading ? 'wait' : 'pointer',
      transition: 'all 0.2s', letterSpacing: '1px', opacity: isLoading ? 0.6 : 1,
    }}>
      {isCopied
        ? <><Check style={{ width: 10, height: 10 }} /> COPIED</>
        : <><Copy style={{ width: 10, height: 10 }} /> {isLoading ? 'LOADING...' : label}</>}
    </button>
  );
};

// ─── Impact Tag ───────────────────────────────────────────────────────────────

const ImpactTag = ({ level }) => {
  const styles = {
    'REVENUE': { bg: `rgba(255,176,32,0.08)`,    color: C.amber,   border: `rgba(255,176,32,0.25)` },
    'GROWTH':  { bg: `rgba(124,63,255,0.08)`,    color: '#b07dff', border: `rgba(124,63,255,0.25)` },
    'SUPPORT': { bg: `rgba(0,168,150,0.08)`,     color: C.tealDim, border: `rgba(0,168,150,0.25)` },
  };
  const s = styles?.[level] || styles?.['SUPPORT'];
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      borderRadius: 3, padding: '3px 7px', ...fontMono, fontSize: 8, fontWeight: 700,
      letterSpacing: '1px', textTransform: 'uppercase',
    }}>{level}</span>
  );
};

// ─── Status Tag ───────────────────────────────────────────────────────────────

const STATUS_TAGS = [
  { label: 'READY FOR DEPLOYMENT', color: C.green,   bg: 'rgba(0,255,136,0.07)',  border: 'rgba(0,255,136,0.25)' },
  { label: 'READY FOR PUBLISHING', color: C.teal,    bg: 'rgba(0,229,200,0.07)',  border: 'rgba(0,229,200,0.25)' },
  { label: 'READY FOR OUTREACH',   color: '#b07dff', bg: 'rgba(124,63,255,0.07)', border: 'rgba(124,63,255,0.25)' },
];

const StatusTag = ({ index }) => {
  const tag = STATUS_TAGS?.[index % STATUS_TAGS?.length];
  return (
    <span style={{
      background: tag.bg, border: `1px solid ${tag.border}`, color: tag.color,
      borderRadius: 3, padding: '3px 9px',
      ...fontOrbitron, fontSize: 7, fontWeight: 700,
      letterSpacing: '1px', textTransform: 'uppercase', flexShrink: 0,
      textShadow: index === 0 ? `0 0 8px ${C.greenGlow}` : 'none',
    }}>{tag.label}</span>
  );
};

// ─── Format Badge ─────────────────────────────────────────────────────────────

const FORMAT_BADGE_COLORS = {
  md:   { bg: `rgba(0,229,200,0.08)`,    color: C.teal,    border: `rgba(0,229,200,0.2)` },
  csv:  { bg: `rgba(255,176,32,0.08)`,   color: C.amber,   border: `rgba(255,176,32,0.2)` },
  json: { bg: `rgba(124,63,255,0.08)`,   color: '#b07dff', border: `rgba(124,63,255,0.2)` },
  txt:  { bg: `rgba(100,100,100,0.08)`,  color: '#888',    border: 'rgba(100,100,100,0.2)' },
  pdf:  { bg: `rgba(255,61,90,0.08)`,    color: C.red,     border: `rgba(255,61,90,0.2)` },
  pptx: { bg: `rgba(255,176,32,0.08)`,   color: C.amber,   border: `rgba(255,176,32,0.2)` },
  docx: { bg: `rgba(0,229,200,0.08)`,    color: C.teal,    border: `rgba(0,229,200,0.2)` },
};

const FormatBadge = ({ format }) => {
  const s = FORMAT_BADGE_COLORS?.[format] || FORMAT_BADGE_COLORS.txt;
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      borderRadius: 3, padding: '2px 7px', ...fontMono,
      fontSize: 8, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', flexShrink: 0,
    }}>
      {format?.toUpperCase()}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const MissionResultsScreen = ({ data, onClose, onPreFillTerminal }) => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [expandedSteps, setExpandedSteps]     = useState({});
  const [filesOpen, setFilesOpen]             = useState(false);
  const [autoMode, setAutoMode]               = useState(false);

  useEffect(() => {
    const loadAutoMode = async () => {
      try {
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch(apiUrl('/api/auto-mode'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) { const json = await res.json(); setAutoMode(json.auto_mode || false); }
      } catch {}
    };
    loadAutoMode();
  }, [session?.access_token]);

  const handleAutoModeToggle = async () => {
    const newState = !autoMode;
    setAutoMode(newState);
    try {
      const token = session?.access_token;
      if (!token) throw new Error('Authentication session is unavailable');
      const res = await fetch(apiUrl('/api/auto-mode/toggle'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newState })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setAutoMode(!newState);
    }
  };
  const [readyFiles, setReadyFiles]           = useState(() => data?.filesReady || null);
  const [downloadingAll, setDownloadingAll]   = useState(false);
  const [selectedFormats, setSelectedFormats] = useState({});
  const [structuredAssets, setStructuredAssets] = useState([]);
  const stepRefs = useRef({});

  // Fetch files from API after mission completes
  useEffect(() => {
    if (!data?.mission_id) return;
    const fetchFiles = async () => {
      try {
        const token = session?.access_token || null;
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(apiUrl(`/api/missions/${data?.mission_id}/files`), { headers });
        if (!res?.ok) return;
        const json = await res?.json();
        const files = json?.files || json;
        if (Array.isArray(files) && files?.length > 0) setReadyFiles(files);
      } catch (err) { console.error('Failed to fetch mission files:', err); }
    };
    fetchFiles();
  }, [data?.mission_id, session?.access_token]);

  // Generate structured assets as client-side fallback
  useEffect(() => {
    if (!data) return;
    const { actionCards = [], completedSteps = {}, intent } = data;
    const assets = generateStructuredAssets(intent || '', intent || '', actionCards, completedSteps);
    if (assets?.length > 0) setStructuredAssets(assets);
  }, [data]);

  // Build grouped deliverables from readyFiles
  const groupedDeliverables = useMemo(() => {
    if (!readyFiles || readyFiles?.length === 0) return null;
    const groups = {};
    readyFiles?.forEach(file => {
      const name = file?.name || '';
      const lastDot = name?.lastIndexOf('.');
      const baseName = lastDot > 0 ? name?.slice(0, lastDot) : name;
      const ext = lastDot > 0 ? name?.slice(lastDot + 1)?.toLowerCase() : '';
      if (!groups?.[baseName]) groups[baseName] = [];
      groups?.[baseName]?.push({ ...file, ext });
    });
    // Synthesize MD (when TXT exists) and CSV (when basename suggests tabular)
    Object.keys(groups).forEach(bn => {
      const files = groups[bn];
      const txtFile = files.find(f => f && f.ext === 'txt');
      if (txtFile) {
        if (!files.find(f => f.ext === 'md')) {
          files.push({ ...txtFile, ext: 'md', _synth: 'md', _src: txtFile });
        }
        if (isLikelyTabular(bn) && !files.find(f => f.ext === 'csv')) {
          files.push({ ...txtFile, ext: 'csv', _synth: 'csv', _src: txtFile });
        }
      }
    });
    return groups;
  }, [readyFiles]);

  // Initialize selectedFormats when structuredAssets changes (Path 2)
  useEffect(() => {
    if (!structuredAssets || structuredAssets.length === 0) return;
    setSelectedFormats(prev => {
      const next = { ...prev };
      let changed = false;
      structuredAssets.forEach(asset => {
        if (asset?.filename && !next[asset.filename]) {
          const tgts = availableTargetFormats(asset?.format);
          next[asset.filename] = asset?.format || tgts?.[0] || 'md';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [structuredAssets]);

  // Initialize selectedFormats when groupedDeliverables changes
  useEffect(() => {
    if (!groupedDeliverables) return;
    setSelectedFormats(prev => {
      const next = { ...prev };
      Object.entries(groupedDeliverables)?.forEach(([baseName, files]) => {
        if (!next?.[baseName]) {
          const hasDocx = files?.find(f => f?.ext === 'docx');
          next[baseName] = hasDocx ? 'docx' : files?.[0]?.ext || '';
        }
      });
      return next;
    });
  }, [groupedDeliverables]);

  if (!data) return null;

  // ─── No results state ─────────────────────────────────────────────────────
  if (data?.noResults) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        style={{ maxWidth: 900, margin: '0 auto', padding: '0 16px 80px', position: 'relative', zIndex: 1, overflowX: 'hidden' }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;500;700;900&display=swap');`}</style>
        <div style={{ background: C.bgPanel, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: C.bgVoid, borderBottom: `1px solid ${C.border}`, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ ...fontMono, fontSize: 11, color: C.teal, letterSpacing: '3px' }}>RECALLCORE // OPERATION CONSOLE</span>
            <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 4, padding: '5px 12px', cursor: 'pointer', color: C.textSec, ...fontMono, fontSize: 9 }}>ESC</button>
          </div>
          <div style={{ padding: '60px 32px', textAlign: 'center', background: C.bgDeep }}>
            <div style={{ ...fontOrbitron, fontSize: 18, fontWeight: 700, color: C.textPrimary, marginBottom: 12 }}>{data?.intent || 'Mission'}</div>
            <p style={{ ...fontGrotesk, fontSize: 14, color: C.textSec, lineHeight: 1.7 }}>No execution assets found for this operation.</p>
          </div>
        </div>
      </motion.div>
    );
  }

  const { actionCards = [], completedSteps = {}, mission_id, steps_completed = 0, steps_failed = 0, intent } = data;

  const actions         = extractActionsFromCards(actionCards, intent);
  const fileRows        = buildFileRows(data);
  const whatYouHave     = generateWhatYouHave(data);
  const whyItMatters    = generateWhyItMatters(data);
  const plainSummary    = generatePlainSummary(data);

  const timeSaved    = (steps_completed * 45 / 60)?.toFixed(1);
    const EXECUTION_VALUE_MAP = { sales_funnel:[3500,8000], marketing_strategy:[2500,6000], content_system:[1800,4500], brand_positioning:[2000,5000], ad_campaign:[1500,3800], business_plan:[3800,9500], outreach_system:[1200,3200], product_launch:[2800,7000], general:[900,2400] };
  const detectCategory = (str='') => { const s=str.toLowerCase(); if(s.includes('sales')&&(s.includes('funnel')||s.includes('pipeline')))return 'sales_funnel'; if(s.includes('marketing')||s.includes('strategy'))return 'marketing_strategy'; if(s.includes('content')||s.includes('social'))return 'content_system'; if(s.includes('brand')||s.includes('position'))return 'brand_positioning'; if(s.includes('campaign')||s.includes('paid ads'))return 'ad_campaign'; if(s.includes('business plan')||s.includes('pitch')||s.includes('investor'))return 'business_plan'; if(s.includes('outreach')||s.includes('cold email'))return 'outreach_system'; if(s.includes('launch')||s.includes('product'))return 'product_launch'; return 'general'; };
  const category = detectCategory(intent);
  const [estValueMin, estValueMax] = EXECUTION_VALUE_MAP[category] || EXECUTION_VALUE_MAP.general;

  const toggleStep  = (i) => setExpandedSteps(prev => ({ ...prev, [i]: !prev?.[i] }));
  const scrollToStep = (i) => {
    setExpandedSteps(prev => ({ ...prev, [i]: true }));
    setTimeout(() => stepRefs?.current?.[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleExport = (row) => {
    const blob = new Blob([row.output || '[No content]'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = row?.filename; a?.click(); URL.revokeObjectURL(url);
  };
  const downloadTextBundle = () => {
    const content = fileRows?.map(r => `=== ${r?.filename} ===\n\n${r?.output || '[No content]'}`)?.join('\n\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `mission-files-${Date.now()}.txt`; a?.click(); URL.revokeObjectURL(url);
  };
  const handleDownloadAll = async () => {
    if (!mission_id || !readyFiles?.length) {
      downloadTextBundle();
      return;
    }

    setDownloadingAll(true);
    try {
      const token = session?.access_token;
      if (!token) throw new Error('Authentication session is unavailable');
      const res = await fetch(apiUrl(`/api/missions/${mission_id}/files/download-all`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed: HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mission-${mission_id}-files.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Mission ZIP download failed; using text fallback:', err);
      downloadTextBundle();
    } finally {
      setDownloadingAll(false);
    }
  };
  // === PATH2-FORMAT-TOGGLES v1 ===
  const handleStructuredAssetDownload = async (asset, overrideFormat) => {
    const fmt = overrideFormat || selectedFormats?.[asset?.filename] || asset?.format || 'md';
    try {
      await convertAndDownload(asset, fmt);
    } catch (err) {
      console.error('Structured asset download error:', err);
      // Fallback: ship raw content with original filename
      const blob = new Blob([asset?.content || ''], { type: asset?.mimeType || 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = asset?.filename; a?.click(); URL.revokeObjectURL(url);
    }
  };
  const handleStructuredDownloadAll = async () => {
    setDownloadingAll(true);
    for (let i = 0; i < structuredAssets?.length; i++) {
      const asset = structuredAssets?.[i];
      const fmt = selectedFormats?.[asset?.filename] || asset?.format || 'md';
      await handleStructuredAssetDownload(asset, fmt);
      if (i < structuredAssets?.length - 1) await new Promise(r => setTimeout(r, 350));
    }
    setDownloadingAll(false);
  };
  const getAuthToken = async () => {
    return session?.access_token || null;
  };
  const handleApiDownload = async (file) => {
    try {
      const token = await getAuthToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(apiUrl(`/api/missions/${mission_id}/files/${encodeURIComponent(file?.name)}`), { headers });
      if (!res?.ok) throw new Error(`Download failed: HTTP ${res.status}`);
      const blob = await res?.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = file?.name; a?.click(); URL.revokeObjectURL(url);
    } catch (err) { console.error('File download error:', err); }
  };
  const fetchFileTextContent = async (file) => {
    const token = await getAuthToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(apiUrl(`/api/missions/${mission_id}/files/${encodeURIComponent(file?.name)}`), { headers });
    if (!res?.ok) throw new Error('Fetch failed: ' + res.status);
    return await res.text();
  };
  const triggerTextDownload = (filename, mime, body) => {
    const blob = new Blob([body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a?.click(); URL.revokeObjectURL(url);
  };
  const handleDeliverableDownload = async (baseName, files) => {
    const selectedExt = selectedFormats?.[baseName];
    const file = files?.find(f => f?.ext === selectedExt) || files?.[0];
    if (!file) return;
    if (file._synth === 'md') {
      try {
        const text = await fetchFileTextContent(file._src);
        triggerTextDownload(`${baseName}.md`, 'text/markdown', text);
      } catch (err) { console.error('MD download error:', err); }
      return;
    }
    if (file._synth === 'csv') {
      try {
        const text = await fetchFileTextContent(file._src);
        const csv = markdownToCsv(text);
        triggerTextDownload(`${baseName}.csv`, 'text/csv', csv);
      } catch (err) { console.error('CSV download error:', err); }
      return;
    }
    await handleApiDownload(file);
  };
  const handleApiDownloadAll = async () => {
    if (!groupedDeliverables) return;
    setDownloadingAll(true);
    const entries = Object.entries(groupedDeliverables);
    for (let i = 0; i < entries?.length; i++) {
      const [baseName, files] = entries?.[i];
      await handleDeliverableDownload(baseName, files);
      if (i < entries?.length - 1) await new Promise(r => setTimeout(r, 300));
    }
    setDownloadingAll(false);
  };

  const hasApiFiles        = readyFiles && readyFiles?.length > 0;
  const hasStructuredAssets = structuredAssets?.length > 0;
  const useApiFiles        = hasApiFiles;
  const useStructuredAssets = !hasApiFiles && hasStructuredAssets;
  const useFallbackRows    = !hasApiFiles && !hasStructuredAssets;

  const displayFileCount = hasApiFiles ? readyFiles?.length : hasStructuredAssets ? structuredAssets?.length : fileRows?.length;

  const FORMAT_ORDER  = ['docx', 'pdf', 'md', 'csv', 'txt', 'pptx'];
  const FORMAT_LABELS = { docx: 'DOCX', pdf: 'PDF', md: 'MD', csv: 'CSV', txt: 'TXT', pptx: 'PPTX' };

  const now     = new Date();
  const dateStr = now?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })?.toUpperCase();
  const timeStr = now?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const metaStr = `${dateStr} · ${timeStr} · ${steps_completed} ACTIONS EXECUTED`;

  const ASSET_TYPES = ['DOC', 'RPT', 'STR', 'PLN', 'ANL', 'CNT'];
  const STRUCTURED_ASSET_ICONS = {
    'sales_playbook.md':                'PLY',
    'follow_up_sequence.csv':           'SEQ',
    'objection_scripts.md':             'OBJ',
    'pipeline_config.json':             'PIP',
    'buyer_qualification_checklist.md': 'QAL',
    'lead_reactivation_sequence.md':    'REA',
    'delivery_handoff_checklist.md':    'DEL',
  };

  // ─── Asset type badge colors ───────────────────────────────────────────────
  const assetBadgeColor = (idx) => {
    const palette = [
      { bg: `rgba(0,229,200,0.1)`,    border: `rgba(0,229,200,0.25)`,    color: C.teal    },
      { bg: `rgba(124,63,255,0.1)`,   border: `rgba(124,63,255,0.25)`,   color: '#b07dff' },
      { bg: `rgba(255,176,32,0.1)`,   border: `rgba(255,176,32,0.25)`,   color: C.amber   },
      { bg: `rgba(0,255,136,0.1)`,    border: `rgba(0,255,136,0.25)`,    color: C.green   },
      { bg: `rgba(255,61,90,0.1)`,    border: `rgba(255,61,90,0.25)`,    color: C.red     },
      { bg: `rgba(0,168,150,0.1)`,    border: `rgba(0,168,150,0.25)`,    color: C.tealDim },
    ];
    return palette[idx % palette.length];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      style={{ maxWidth: 900, margin: '0 auto', padding: '0 16px 80px', position: 'relative', zIndex: 1, overflowX: 'hidden' }}
    >
      {/* ── Fonts + Animations ──────────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;500;700;900&display=swap');

        @keyframes pulseDot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(0.85); opacity: 0.6; }
        }
        @keyframes pulseGreen {
          0%, 100% { transform: scale(1); box-shadow: 0 0 6px ${C.green}; }
          50%       { transform: scale(1.15); box-shadow: 0 0 14px ${C.green}; }
        }
        @keyframes pulseGreenDot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.2); opacity: 0.75; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.25; }
        }
        .exec-item:hover           { background: ${C.bgCardHover} !important; }
        .pipeline-card:hover .pipeline-header { background: ${C.bgCardHover} !important; }
        .asset-row:hover           { background: ${C.bgCardHover} !important; }
        .structured-asset-row:hover { background: ${C.bgCardHover} !important; }
        ::-webkit-scrollbar        { width: 4px; }
        ::-webkit-scrollbar-track  { background: ${C.bgVoid}; }
        ::-webkit-scrollbar-thumb  { background: ${C.tealGlow}; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: ${C.tealDim}; }
        @media (max-width: 640px) {
          .rc-metrics-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .rc-hero-row { flex-direction: column !important; gap: 16px !important; }
          .rc-hero-right { align-items: flex-start !important; flex-direction: row !important; flex-wrap: wrap !important; gap: 8px !important; }
        }
      `}</style>

      <div style={{ background: C.bgPanel, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}` }}>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TOP BAR */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div style={{
          background: C.bgVoid,
          borderBottom: `1px solid ${C.border}`,
          padding: '11px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ ...fontMono, fontSize: 11, color: C.teal, letterSpacing: '3px', textShadow: `0 0 16px rgba(0,229,200,0.4)` }}>
            RECALLCORE // OPERATION CONSOLE
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* AUTO MODE toggle */}
            <button
              onClick={handleAutoModeToggle}
              style={{
                background: autoMode ? `rgba(0,255,136,0.06)` : C.bgPanel,
                border: `1px solid ${autoMode ? 'rgba(0,255,136,0.3)' : C.border}`,
                borderRadius: 4, padding: '6px 14px',
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: autoMode ? C.green : C.textDim,
                border: `1px solid ${autoMode ? C.green : C.textSec}`,
                animation: autoMode ? 'pulseGreenDot 2s infinite' : 'none',
                transition: 'background 0.2s',
              }} />
              <span style={{ ...fontMono, fontSize: 9, color: autoMode ? C.green : C.textSec, letterSpacing: '1.5px' }}>
                AUTO MODE: {autoMode ? 'ON' : 'OFF'}
              </span>
            </button>
            {/* VORTEX LIVE badge */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '6px 13px',
              border: `1px solid ${C.borderPurple}`,
              borderRadius: 4,
              background: C.purpleGlow,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.red, boxShadow: `0 0 8px ${C.red}`, animation: 'pulseDot 1s infinite' }} />
              <span style={{ ...fontMono, fontSize: 9, color: '#cc6dff', letterSpacing: '1.5px' }}>LIVE</span>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent', border: `1px solid ${C.border}`,
                borderRadius: 4, padding: '6px 14px', cursor: 'pointer',
                color: C.textSec, ...fontMono, fontSize: 9, letterSpacing: '1px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textSec; }}
            >
              ESC
            </button>
          </div>
        </div>

        {/* ── OPERATION COMPLETE status strip ─────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '11px 20px',
          background: `linear-gradient(90deg, rgba(0,255,136,0.06) 0%, transparent 60%)`,
          borderBottom: `1px solid rgba(0,255,136,0.12)`,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: C.green, boxShadow: `0 0 10px ${C.green}`,
            animation: 'pulseGreen 1.2s infinite',
          }} />
          <span style={{ ...fontMono, fontSize: 10, color: C.green, letterSpacing: '3px', fontWeight: 700, textShadow: `0 0 12px rgba(0,255,136,0.4)` }}>
            OPERATION COMPLETE
          </span>
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* SCROLLABLE CONTENT */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div style={{ maxHeight: '85vh', overflowY: 'auto', background: C.bgDeep }}>

          {/* ── HERO SECTION ─────────────────────────────────────────────── */}
          <div style={{ background: C.bgPanel, borderBottom: `1px solid ${C.border}` }}>
            <div className="rc-hero-row" style={{ display: 'flex', gap: 24, padding: '24px 24px 0' }}>
              {/* LEFT */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Eyebrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, animation: 'pulseGreen 2s infinite', flexShrink: 0 }} />
                  <span style={{ ...fontMono, fontSize: 9, color: C.green, letterSpacing: '3px' }}>OPERATION COMPLETE</span>
                </div>
                {/* Title */}
                <h1 style={{ ...fontOrbitron, fontSize: 22, fontWeight: 700, color: C.textPrimary, letterSpacing: '0.5px', lineHeight: 1.25, marginBottom: 10 }}>
                  {intent || 'Operation Complete'}
                </h1>
                {/* Summary */}
                <FormattedSummary text={plainSummary} style={{ ...fontGrotesk, fontSize: 13, color: C.textSec, lineHeight: 1.65, marginBottom: 12, fontWeight: 500 }} />
                {/* Meta */}
                <div style={{ ...fontMono, fontSize: 9, color: C.textDim, letterSpacing: '1px' }}>
                  {metaStr}
                </div>
              </div>

              {/* RIGHT */}
              <div className="rc-hero-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
                {/* LIVE block */}
                <div style={{
                  border: `1px solid ${C.borderPurple}`, borderRadius: 8,
                  background: C.bgCard,
                  padding: '14px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: `2px solid ${C.teal}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 16px ${C.tealGlow}`,
                  }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: C.red, animation: 'pulseDot 1s infinite' }} />
                  </div>
                  <span style={{ ...fontMono, fontSize: 8, color: C.teal, letterSpacing: '3px' }}>LIVE</span>
                </div>
                {/* Revenue badge */}
                <div style={{
                  background: C.bgCard,
                  border: `1px solid rgba(255,176,32,0.25)`,
                  borderRadius: 6, padding: '8px 14px', textAlign: 'center',
                }}>
                  <div style={{ ...fontOrbitron, fontSize: 18, fontWeight: 700, color: C.amber, textShadow: `0 0 16px ${C.amberGlow}` }}>
                    ${estValueMin}–${estValueMax}
                  </div>
                  <div style={{ ...fontMono, fontSize: 8, color: `rgba(255,176,32,0.4)`, letterSpacing: '2px', marginTop: 3 }}>
                    EXECUTION VALUE
                  </div>
                </div>
              </div>
            </div>

            {/* METRICS ROW */}
            <div className="rc-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderTop: `1px solid ${C.border}`, marginTop: 20 }}>
              {[
                { label: 'TIME COMPRESSED',   value: `${timeSaved}h`,          sub: 'vs doing manually', color: C.teal   },
                { label: 'EXECUTION VALUE', value: `$${estValueMax}`,        sub: 'equivalent freelancer cost',   color: C.amber  },
                { label: 'ACTIONS EXECUTED',  value: `${steps_completed}`,     sub: 'all verified',      color: C.green  },
                { label: 'ASSETS READY',      value: `${displayFileCount || steps_completed}`, sub: 'deploy now', color: C.purple },
              ]?.map((m, i) => (
                <div key={i} style={{
                  padding: '16px 20px',
                  borderRight: i < 3 ? `1px solid ${C.border}` : 'none',
                  background: C.bgCard,
                  transition: 'background 0.2s',
                }}>
                  <div style={{ ...fontMono, fontSize: 8, color: C.textDim, letterSpacing: '1.5px', marginBottom: 6 }}>{m.label}</div>
                  <div style={{ ...fontOrbitron, fontSize: 24, fontWeight: 700, color: m.color, letterSpacing: '-1px', lineHeight: 1, textShadow: `0 0 20px ${m.color}40` }}>
                    {m.value}
                  </div>
                  <div style={{ ...fontMono, fontSize: 9, color: C.textSec, marginTop: 5 }}>{m.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── CONTENT ──────────────────────────────────────────────────── */}
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* NEXT COMMAND */}
            {actions?.length > 0 && (
              <div style={{
                border: `1px solid rgba(124,63,255,0.35)`,
                borderRadius: 8,
                background: `linear-gradient(135deg, rgba(124,63,255,0.07) 0%, rgba(0,229,200,0.03) 100%)`,
                padding: '18px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                position: 'relative', overflow: 'hidden',
              }}>
                {/* Left accent bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: C.purple, boxShadow: `0 0 12px rgba(124,63,255,0.5)` }} />
                <div style={{ flex: 1, minWidth: 0, paddingLeft: 8 }}>
                  <div style={{ ...fontMono, fontSize: 9, color: C.purple, letterSpacing: '3px', marginBottom: 6 }}>NEXT COMMAND</div>
                  <div style={{ ...fontGrotesk, fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 6, lineHeight: 1.4 }}>
                    {actions?.[0]?.title}
                  </div>
                  <div style={{ ...fontMono, fontSize: 9, color: C.textDim }}>EXECUTES IMMEDIATELY · MOVES OPERATION FORWARD</div>
                </div>
                <button
                  onClick={() => scrollToStep(0)}
                  style={{
                    background: `rgba(0,229,200,0.07)`, border: `1px solid ${C.teal}`,
                    borderRadius: 5, padding: '12px 22px',
                    ...fontMono, fontSize: 11, fontWeight: 700, color: C.teal,
                    cursor: 'pointer', letterSpacing: '2px', flexShrink: 0,
                    transition: 'all 0.2s', textShadow: `0 0 10px rgba(0,229,200,0.4)`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = `rgba(0,229,200,0.15)`; e.currentTarget.style.boxShadow = `0 0 20px rgba(0,229,200,0.18)`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = `rgba(0,229,200,0.07)`; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  EXECUTE NOW →
                </button>
              </div>
            )}

            {/* REVENUE STRIP */}
            <div style={{
              background: `linear-gradient(90deg, rgba(255,176,32,0.07) 0%, transparent 100%)`,
              border: `1px solid rgba(255,176,32,0.22)`,
              borderRadius: 5, padding: '10px 18px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ ...fontGrotesk, fontSize: 15, fontWeight: 700, color: C.amber, textShadow: `0 0 14px ${C.amberGlow}` }}>
                Execution value: <span>${estValueMin}–${estValueMax}</span>
              </div>
              <div style={{ ...fontMono, fontSize: 9, color: `rgba(255,176,32,0.4)` }}>
                {Math.max(1, Math.floor(steps_completed / 2))} freelancers replaced · ROI MULTIPLIER: 12×
              </div>
            </div>

            {/* ── GLOW SEPARATOR ───────────────────────────────────────────── */}
            <div style={{ width: '100%', height: 1, background: `linear-gradient(90deg, transparent, rgba(0,229,200,0.2), rgba(124,63,255,0.2), transparent)` }} />

            {/* SYSTEM EXECUTIONS */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <div style={{ width: 3, height: 12, background: C.teal, borderRadius: 2, boxShadow: `0 0 8px rgba(0,229,200,0.6)` }} />
                  <span style={{ ...fontMono, fontSize: 9, color: C.textSec, letterSpacing: '3px' }}>SYSTEM EXECUTIONS</span>
                </div>
                <span style={{ ...fontMono, fontSize: 9, color: C.teal, background: `rgba(0,229,200,0.07)`, border: `1px solid rgba(0,229,200,0.2)`, padding: '2px 10px', borderRadius: 10 }}>
                  {steps_completed} COMPLETED
                </span>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden' }}>
                {whatYouHave?.map((bullet, i) => (
                  <div
                    key={i}
                    className="exec-item"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '11px 14px',
                      borderBottom: i < whatYouHave?.length - 1 ? `1px solid ${C.border}` : 'none',
                      background: C.bgCard, transition: 'background 0.15s',
                    }}
                  >
                    <span style={{ ...fontMono, fontSize: 9, color: C.textDim, width: 18, flexShrink: 0 }}>
                      {String(i + 1)?.padStart(2, '0')}
                    </span>
                    <div style={{ width: 2, height: 28, background: C.teal, opacity: 0.3, flexShrink: 0, borderRadius: 1 }} />
                    <span style={{ ...fontGrotesk, fontSize: 12, fontWeight: 500, color: C.textPrimary, flex: 1 }}>
                      {bullet}
                    </span>
                    <StatusTag index={i} />
                  </div>
                ))}
              </div>
            </div>

            {/* ── GLOW SEPARATOR ───────────────────────────────────────────── */}
            <div style={{ width: '100%', height: 1, background: `linear-gradient(90deg, transparent, rgba(0,229,200,0.2), rgba(124,63,255,0.2), transparent)` }} />

            {/* EXECUTION PIPELINE */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <div style={{ width: 3, height: 12, background: C.teal, borderRadius: 2, boxShadow: `0 0 8px rgba(0,229,200,0.6)` }} />
                  <span style={{ ...fontMono, fontSize: 9, color: C.textSec, letterSpacing: '3px' }}>EXECUTION PIPELINE</span>
                </div>
                <span style={{ ...fontMono, fontSize: 9, color: C.textDim }}>TAP TO EXPAND</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {actions?.map((action, i) => {
                  const isOpen = !!expandedSteps?.[i];
                  const paragraphs = formatOutputAsReadable(action?.script);
                  return (
                    <div
                      key={i}
                      ref={el => { stepRefs.current[i] = el; }}
                      className="pipeline-card"
                      style={{
                        border: `1px solid ${i === 0 ? C.borderBright : C.border}`,
                        borderRadius: 5, background: C.bgCard, overflow: 'hidden',
                      }}
                    >
                      <button
                        onClick={() => toggleStep(i)}
                        className="pipeline-header"
                        style={{
                          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                          padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
                          textAlign: 'left', transition: 'background 0.15s',
                        }}
                      >
                        <span style={{ ...fontMono, fontSize: 9, color: C.textDim, width: 22, flexShrink: 0 }}>
                          {String(i + 1)?.padStart(2, '0')} ›
                        </span>
                        <span style={{ ...fontGrotesk, fontSize: 12, fontWeight: 500, color: C.textPrimary, flex: 1 }}>
                          {action?.title}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <ImpactTag level={action?.impact} />
                          <span style={{ ...fontMono, fontSize: 8, color: C.textSec }}>{action?.time}</span>
                          <button
                            onClick={(e) => { e?.stopPropagation(); onPreFillTerminal?.(action?.followUp); }}
                            style={{
                              background: `rgba(0,229,200,0.05)`, border: `1px solid ${C.teal}`,
                              borderRadius: 3, padding: '4px 10px',
                              ...fontMono, fontSize: 9, color: C.teal,
                              cursor: 'pointer', letterSpacing: '1.5px', transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = `rgba(0,229,200,0.14)`; }}
                            onMouseLeave={e => { e.currentTarget.style.background = `rgba(0,229,200,0.05)`; }}
                          >
                            EXECUTE
                          </button>
                          {isOpen
                            ? <ChevronUp   style={{ width: 13, height: 13, color: C.textSec }} />
                            : <ChevronDown style={{ width: 13, height: 13, color: C.textSec }} />
                          }
                        </div>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                            style={{ overflow: 'hidden' }}
                          >
                            <div style={{
                              background: `rgba(0,229,200,0.02)`,
                              borderTop: `1px solid ${C.border}`,
                              padding: '10px 14px 14px 46px',
                            }}>
                              <p style={{ ...fontGrotesk, fontSize: 11, color: C.textSec, lineHeight: 1.75, marginBottom: 10 }}>
                                {action?.description}
                              </p>
                              {paragraphs?.length > 0 && (
                                <div style={{ marginBottom: 10 }}>
                                  {paragraphs?.map((para, pi) => (
                                    <p key={pi} style={{ ...fontGrotesk, fontSize: 11, color: C.textSec, lineHeight: 1.75, marginBottom: pi < paragraphs?.length - 1 ? 6 : 0 }}>
                                      {para}
                                    </p>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 6 }}>
                                <CopyButton text={cleanMarkdown(action?.script)} label="COPY" />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── GLOW SEPARATOR ───────────────────────────────────────────── */}
            <div style={{ width: '100%', height: 1, background: `linear-gradient(90deg, transparent, rgba(0,229,200,0.2), rgba(124,63,255,0.2), transparent)` }} />

            {/* DEPLOYABLE ASSETS */}
            <div style={{ background: C.bgCard, borderRadius: 6, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              <button
                onClick={() => setFilesOpen(v => !v)}
                style={{
                  width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '13px 16px', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', textAlign: 'left',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `rgba(0,229,200,0.03)`; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 3, height: 12, background: C.teal, borderRadius: 2, boxShadow: `0 0 8px rgba(0,229,200,0.6)` }} />
                  <span style={{ ...fontMono, fontSize: 9, color: C.teal, letterSpacing: '3px' }}>
                    DEPLOYABLE ASSETS
                  </span>
                  <span style={{
                    background: `rgba(0,229,200,0.1)`, border: `1px solid rgba(0,229,200,0.25)`,
                    padding: '1px 9px', borderRadius: 10, fontSize: 9, color: C.teal, ...fontMono,
                  }}>
                    {displayFileCount || steps_completed} READY
                  </span>
                </div>
                {filesOpen
                  ? <ChevronUp   style={{ width: 13, height: 13, color: C.textSec }} />
                  : <ChevronDown style={{ width: 13, height: 13, color: C.textSec }} />
                }
              </button>

              <AnimatePresence>
                {filesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{ borderTop: `1px solid ${C.border}` }}>

                      {/* PATH 1: API-backed files */}
                      {useApiFiles && groupedDeliverables && Object.keys(groupedDeliverables)?.length > 0 ? (
                        <>
                          {Object.entries(groupedDeliverables)?.map(([baseName, files], idx) => {
                            const availableFormats = FORMAT_ORDER?.filter(fmt => files?.some(f => f?.ext === fmt));
                            const selectedExt = selectedFormats?.[baseName] || availableFormats?.[0] || '';
                            const typeLabel = ASSET_TYPES?.[idx % ASSET_TYPES?.length];
                            const badge = assetBadgeColor(idx);
                            return (
                              <div key={baseName} className="asset-row" style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                                borderBottom: `1px solid ${C.border}`,
                                background: C.bgCard, transition: 'background 0.15s', flexWrap: 'wrap',
                              }}>
                                <div style={{
                                  width: 34, height: 34, background: badge.bg,
                                  border: `1px solid ${badge.border}`, borderRadius: 4,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <span style={{ ...fontMono, fontSize: 8, fontWeight: 700, color: badge.color }}>{typeLabel}</span>
                                </div>
                                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                                  <div style={{ ...fontGrotesk, fontSize: 12, fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {baseName}
                                  </div>
                                  <div style={{ ...fontMono, fontSize: 8, color: C.textDim, marginTop: 2 }}>
                                    {availableFormats?.map(f => FORMAT_LABELS?.[f])?.join(' · ')}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {availableFormats?.map(fmt => {
                                    const isSel = selectedExt === fmt;
                                    return (
                                      <button key={fmt}
                                        onClick={() => setSelectedFormats(prev => ({ ...prev, [baseName]: fmt }))}
                                        style={{
                                          background: isSel ? `rgba(0,229,200,0.1)` : 'transparent',
                                          border: `1px solid ${isSel ? C.teal : C.border}`,
                                          borderRadius: 3, padding: '3px 7px',
                                          ...fontMono, fontSize: 8, fontWeight: 700,
                                          color: isSel ? C.teal : C.textDim,
                                          cursor: 'pointer', transition: 'all 0.15s',
                                        }}
                                      >
                                        {FORMAT_LABELS?.[fmt] || fmt?.toUpperCase()}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                                  <button onClick={() => handleDeliverableDownload(baseName, files)} style={{
                                    background: `rgba(0,255,136,0.07)`, border: `1px solid ${C.green}`,
                                    borderRadius: 3, padding: '5px 10px', ...fontMono, fontSize: 9, color: C.green,
                                    cursor: 'pointer', letterSpacing: '1.5px', fontWeight: 700,
                                    textShadow: `0 0 8px rgba(0,255,136,0.35)`,
                                  }}>⬇ DOWNLOAD</button>
                                  <NotionCopyButton getContent={async () => {
                                    const txt = files.find(f => f && f.ext === 'txt');
                                    if (!txt) return baseName;
                                    try { return await fetchFileTextContent(txt); } catch (e) { return ''; }
                                  }} label="COPY NOTION" />
                                </div>
                              </div>
                            );
                          })}
                          <button onClick={handleApiDownloadAll} disabled={downloadingAll} style={{
                            width: '100%', background: 'none', border: 'none',
                            borderTop: `1px solid ${C.border}`, padding: '12px',
                            ...fontMono, fontSize: 9, color: C.teal,
                            cursor: downloadingAll ? 'not-allowed' : 'pointer', letterSpacing: '2px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            transition: 'opacity 0.15s', opacity: downloadingAll ? 0.5 : 1,
                          }}>
                            ↓ {downloadingAll ? 'DOWNLOADING...' : 'DOWNLOAD ALL ASSETS'}
                          </button>
                        </>

                      ) : useStructuredAssets ? (
                        <>
                          {structuredAssets?.map((asset, i) => {
                            const iconLabel = STRUCTURED_ASSET_ICONS?.[asset?.filename] || ASSET_TYPES?.[i % ASSET_TYPES?.length];
                            const badge = assetBadgeColor(i);
                            const targetFormats = availableTargetFormats(asset?.format);
                            const selectedFmt = selectedFormats?.[asset?.filename] || asset?.format || targetFormats?.[0];
                            return (
                              <div key={asset?.filename} className="structured-asset-row" style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                                borderBottom: i < structuredAssets?.length - 1 ? `1px solid ${C.border}` : 'none',
                                background: C.bgCard, transition: 'background 0.15s', flexWrap: 'wrap',
                              }}>
                                <div style={{
                                  width: 34, height: 34, background: badge.bg, border: `1px solid ${badge.border}`,
                                  borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <span style={{ ...fontMono, fontSize: 7, fontWeight: 700, color: badge.color }}>{iconLabel}</span>
                                </div>
                                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                                  <div style={{ ...fontGrotesk, fontSize: 12, fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {asset?.filename}
                                  </div>
                                  <div style={{ ...fontGrotesk, fontSize: 10, color: C.textSec, lineHeight: 1.4, marginTop: 2 }}>
                                    {asset?.description}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {targetFormats?.map(fmt => {
                                    const isSel = selectedFmt === fmt;
                                    return (
                                      <button key={fmt}
                                        onClick={() => setSelectedFormats(prev => ({ ...prev, [asset?.filename]: fmt }))}
                                        style={{
                                          background: isSel ? `rgba(0,229,200,0.1)` : 'transparent',
                                          border: `1px solid ${isSel ? C.teal : C.border}`,
                                          borderRadius: 3, padding: '3px 7px',
                                          ...fontMono, fontSize: 8, fontWeight: 700,
                                          color: isSel ? C.teal : C.textDim,
                                          cursor: 'pointer', transition: 'all 0.15s',
                                        }}
                                      >
                                        {(FORMAT_LABELS?.[fmt]) || fmt?.toUpperCase()}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                                  <button onClick={() => handleStructuredAssetDownload(asset, selectedFmt)} style={{
                                    background: `rgba(0,255,136,0.07)`, border: `1px solid ${C.green}`,
                                    borderRadius: 3, padding: '5px 10px', ...fontMono, fontSize: 9, color: C.green,
                                    cursor: 'pointer', letterSpacing: '1.5px', fontWeight: 700,
                                    textShadow: `0 0 8px rgba(0,255,136,0.35)`,
                                  }}>⬇ DOWNLOAD</button>
                                  <NotionCopyButton getContent={asset?.content || ''} label="COPY NOTION" />
                                </div>
                              </div>
                            );
                          })}
                          <button onClick={handleStructuredDownloadAll} disabled={downloadingAll} style={{
                            width: '100%', background: 'none', border: 'none',
                            borderTop: `1px solid ${C.border}`, padding: '12px',
                            ...fontMono, fontSize: 9, color: C.teal,
                            cursor: downloadingAll ? 'not-allowed' : 'pointer', letterSpacing: '2px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            transition: 'opacity 0.15s', opacity: downloadingAll ? 0.5 : 1,
                          }}>
                            ↓ {downloadingAll ? 'DOWNLOADING...' : 'DOWNLOAD ALL ASSETS'}
                          </button>
                        </>

                      ) : useFallbackRows && fileRows?.length === 0 ? (
                        <div style={{ padding: '24px', textAlign: 'center', ...fontGrotesk, fontSize: 13, color: C.textSec }}>
                          No assets were ready for this operation.
                        </div>
                      ) : (
                        <>
                          {fileRows?.map((row, i) => {
                            const badge = assetBadgeColor(i);
                            return (
                              <div key={i} className="asset-row" style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                                borderBottom: `1px solid ${C.border}`,
                                background: C.bgCard, transition: 'background 0.15s',
                              }}>
                                <div style={{
                                  width: 34, height: 34, background: badge.bg, border: `1px solid ${badge.border}`,
                                  borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <span style={{ ...fontMono, fontSize: 8, fontWeight: 700, color: badge.color }}>
                                    {ASSET_TYPES?.[i % ASSET_TYPES?.length]}
                                  </span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ ...fontGrotesk, fontSize: 12, fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row?.title || row?.filename}
                                  </div>
                                  <div style={{ ...fontMono, fontSize: 8, color: C.textDim, marginTop: 2 }}>{row?.size}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                                  <button onClick={() => handleExport(row)} style={{
                                    background: `rgba(0,255,136,0.07)`, border: `1px solid ${C.green}`,
                                    borderRadius: 3, padding: '5px 10px', ...fontMono, fontSize: 9, color: C.green,
                                    cursor: 'pointer', letterSpacing: '1.5px', fontWeight: 700,
                                    textShadow: `0 0 8px rgba(0,255,136,0.35)`,
                                  }}>⬇ DOWNLOAD</button>
                                  <NotionCopyButton getContent={row?.output || row?.title || ''} label="COPY NOTION" />
                                </div>
                              </div>
                            );
                          })}
                          <button onClick={handleDownloadAll} style={{
                            width: '100%', background: 'none', border: 'none',
                            borderTop: `1px solid ${C.border}`, padding: '12px',
                            ...fontMono, fontSize: 9, color: C.teal,
                            cursor: 'pointer', letterSpacing: '2px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          }}>
                            ↓ DOWNLOAD ALL ASSETS
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* SYSTEM STATUS */}
            <div style={{
              background: C.bgCard, border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${C.purple}`, borderRadius: '0 5px 5px 0',
              padding: '14px 18px',
            }}>
              <div style={{ ...fontMono, fontSize: 9, color: C.purple, letterSpacing: '3px', marginBottom: 12 }}>
                SYSTEM STATUS
              </div>
              {[
                'Monitoring engagement patterns across platforms',
                'Preparing next content batch optimization',
                'Analyzing platform algorithm changes',
              ]?.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  paddingTop: i > 0 ? 10 : 0, paddingBottom: i < 2 ? 10 : 0,
                  borderBottom: i < 2 ? `1px solid ${C.border}` : 'none',
                }}>
                  <div style={{
                    width: 5, height: 5, borderRadius: '50%', background: C.tealDim,
                    boxShadow: `0 0 5px rgba(0,168,150,0.5)`,
                    animation: `blink 2.5s infinite`,
                    animationDelay: `${i * 0.8}s`, flexShrink: 0,
                  }} />
                  <span style={{ ...fontGrotesk, fontSize: 11, color: C.textSec, fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>
            {/* COMMAND INTEL BANNER */}
            <div style={{
              margin: '16px 0 0',
              background: 'linear-gradient(135deg, rgba(0,229,200,0.04) 0%, rgba(124,63,255,0.04) 100%)',
              border: `1px solid ${C.borderBright}`,
              borderLeft: `3px solid ${C.teal}`,
              borderRadius: '0 6px 6px 0',
              padding: '16px 20px',
            }}>
              <div style={{ ...fontMono, fontSize: 9, color: C.teal, letterSpacing: '3px', marginBottom: 10 }}>
                COMMAND INTEL
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...fontGrotesk, fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 4 }}>
                    Mission complete. What is your next move?
                  </div>
                  <div style={{ ...fontMono, fontSize: 9, color: C.textDim }}>3 high-leverage actions ready</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>

                  <button onClick={() => navigate('/lead-command-center')} style={{ background: 'rgba(124,63,255,0.08)', border: '1px solid rgba(124,63,255,0.6)', borderRadius: 4, color: '#b07dff', ...fontMono, fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', padding: '8px 14px', cursor: 'pointer', textTransform: 'uppercase' }}>
                    DEPLOY TO SALES CREW
                  </button>
                  <button onClick={() => onPreFillTerminal && onPreFillTerminal('Follow-up mission: ' + (intent || 'continue execution'))} style={{ background: 'rgba(255,176,32,0.08)', border: '1px solid rgba(255,176,32,0.5)', borderRadius: 4, color: C.amber, ...fontMono, fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', padding: '8px 14px', cursor: 'pointer', textTransform: 'uppercase' }}>
                    RUN FOLLOW-UP MISSION
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MissionResultsScreen;

